import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, PeopleAlt as PeopleIcon } from '@mui/icons-material';
import {
  DepotInput,
  DepotSummary,
  TenantProfile,
  UserAccount,
} from '../../../models/userManagement';
import DepotDialog from '../dialogs/DepotDialog';
import DepotUsersDrawer from '../dialogs/DepotUsersDrawer';

interface DepotsTabProps {
  depots: DepotSummary[];
  tenants: TenantProfile[];
  users: UserAccount[];
  loading: boolean;
  canEdit: boolean;
  onCreateDepot: (payload: DepotInput) => Promise<void> | void;
  onUpdateDepot: (depotId: string, payload: DepotInput) => Promise<void> | void;
}

const DepotsTab: React.FC<DepotsTabProps> = ({ depots, tenants, users, loading, canEdit, onCreateDepot, onUpdateDepot }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepot, setEditingDepot] = useState<DepotSummary | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDepot, setSelectedDepot] = useState<DepotSummary | undefined>(undefined);

  const sortedDepots = useMemo(() => [...depots].sort((a, b) => a.name.localeCompare(b.name)), [depots]);

  const tenantLookup = useMemo(() => {
    const map = new Map<string, string>();
    tenants.forEach((tenant) => map.set(tenant.id, tenant.name));
    return map;
  }, [tenants]);

  const handleNewDepot = () => {
    setEditingDepot(undefined);
    setDialogOpen(true);
  };

  const handleEditDepot = (depot: DepotSummary) => {
    setEditingDepot(depot);
    setDialogOpen(true);
  };

  const handleManageUsers = (depot: DepotSummary) => {
    setSelectedDepot(depot);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: DepotInput) => {
    if (editingDepot) {
      await onUpdateDepot(editingDepot.depotId, payload);
    } else {
      await onCreateDepot(payload);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Depots</Typography>
        {canEdit && (
          <Button variant="contained" onClick={handleNewDepot}>
            Add Depot
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Model Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Users</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDepots.map((depot) => (
                <TableRow key={depot.depotId} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{depot.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {depot.depotId}
                    </Typography>
                  </TableCell>
                  <TableCell>{tenantLookup.get(depot.tenantId) || depot.tenantId}</TableCell>
                  <TableCell>{depot.modelType}</TableCell>
                  <TableCell>
                    <Chip
                      label={depot.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={depot.active ? 'success' : 'default'}
                      variant={depot.active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{depot.assignedUserCount ?? '—'}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title="Manage depot users">
                        <IconButton size="small" onClick={() => handleManageUsers(depot)}>
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit depot">
                        <IconButton size="small" onClick={() => handleEditDepot(depot)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {sortedDepots.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No depots available.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DepotDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tenants={tenants}
        initialValues={editingDepot}
        onSubmit={handleSubmit}
        defaultTenantId={tenants.length === 1 ? tenants[0].id : undefined}
      />

      <DepotUsersDrawer
        open={drawerOpen}
        depot={selectedDepot}
        tenants={tenants}
        users={users}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};

export default DepotsTab;
